<script setup lang="ts">
import { ref, watchEffect, toRefs, computed, watch } from 'vue';
import { useEditUser, CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import PermissionLock from '../../PermissionLock.vue';
import RoleSelect from '../../RoleSelect.vue';
import { useCurrentOrganization } from '../../../composables/useCurrentOrganization';
import useUserValidation from './useUserValidation';
import useToast from '../../../composables/useToast';
import { useLocale } from '../../../composables/useLocale';
import useModal, { modals } from '../../../composables/useModal';
import { Role } from '../../../types/user';
import { usePermissions } from '../../../composables/usePermissions';
import {
  checkAssociationUpdatePermission,
  checkUserUpdatePermission,
  checkSuperUserPermission,
} from '@ilteducation/customer-mgmt-permissions';
import { RoleOption, useUserRoles } from '../../../composables/useUserRoles';
import { useAppEvents } from '../../../events/appEvents';

const { t } = useLocale();

type FormUser = {
  firstName: string;
  lastName: string;
  role: Role;
  email: string;
  sharedAccount?: boolean;
};

type Props = {
  user: CustomerMgmtTypes.GetUserResponse;
  organizationUid: string;
};

const props = defineProps<Props>();
const { user, organizationUid } = toRefs(props);

const { organizations, organization } = useCurrentOrganization();

const { getRoleOptionsToUpdateTo } = useUserRoles();
const {
  permissionContext,
  createTargetAssociation,
  getCallerAssociation,
  getCallerAssociations,
  getGroupMemberships,
  createGetTargetAssociations,
  reporterWithReason,
  reporterBoolean,
} = usePermissions();
const { emitAppEvent } = useAppEvents();

const { open: openManageOrganizationsModal } = useModal(modals.MANAGE_USER_ORGANIZATIONS);

const roleOptions = ref<RoleOption[]>([]);
watch(
  [organization, user],
  async () => {
    return getRoleOptionsToUpdateTo(user.value, organization.value).then((options) => {
      roleOptions.value = options;
    });
  },
  { immediate: true },
);

const isAllowedToManageUserOrganizations = computed(() => {
  return organizations.value.some(
    (org) => ['admin', 'employee'].includes(org.role) && org.uid !== organization.value.uid,
  );
});

const formRef = ref();
const message = ref('');
const error = ref(false);
const isLoading = ref(false);
const { rules, formUser } = useUserValidation<FormUser>();
const { updateUser, updateAssociation } = useEditUser();
const { toast } = useToast();

const getUserRole = () => {
  const association = user.value.associations?.find((unit) => unit.schoolUnit.uid === organizationUid.value);
  return (association?.isAdministrator ? 'admin' : association?.role) ?? 'student';
};

const cloneUserData = () => {
  return {
    firstName: user.value.firstName,
    lastName: user.value.lastName,
    role: getUserRole(),
    email: user.value.email,
    sharedAccount: user.value.sharedAccount,
  };
};
watchEffect(() => {
  formUser.value = cloneUserData() as FormUser;
});

const resetForm = () => {
  formUser.value = cloneUserData() as FormUser;
};

const resetValidation = () => {
  formRef.value.resetValidation();
};

const isUserChanged = () => {
  return (
    formUser.value.firstName !== user.value.firstName ||
    formUser.value.lastName !== user.value.lastName ||
    formUser.value.email !== user.value.email ||
    formUser.value.sharedAccount !== user.value.sharedAccount
  );
};

const isUserAssociationChanged = () => {
  return formUser.value.role !== getUserRole();
};

type UpdateUserAndAssociationResult = {
  success: boolean;
  updated: boolean;
};

const updateUserAndAssociation = async (): Promise<UpdateUserAndAssociationResult> => {
  if (!formRef.value.isValid) {
    return { success: false, updated: false };
  }
  if (!isUserChanged() && !isUserAssociationChanged()) {
    toast(t('lcp.no_changes_saved'), 'info');
    return { success: true, updated: false };
  }

  isLoading.value = true;
  let updated = false;

  if (isUserChanged()) {
    const updateUserSuccess = await updateUser(user.value.uid!, {
      firstName:
        formUser.value.firstName?.trim()?.toLowerCase() !== user.value.firstName?.trim()?.toLowerCase()
          ? formUser.value.firstName?.trim()?.toLowerCase()
          : undefined,
      lastName:
        formUser.value.lastName?.trim()?.toLowerCase() !== user.value.lastName.trim()?.toLowerCase()
          ? formUser.value.lastName?.trim()?.toLowerCase()
          : undefined,
      email:
        formUser.value.email?.trim()?.toLowerCase() !== user.value.email?.trim()?.toLowerCase()
          ? formUser.value.email?.trim()?.toLowerCase()
          : undefined,
      guest: formUser.value.sharedAccount !== user.value.sharedAccount ? formUser.value.sharedAccount : undefined,
    });

    if (!updateUserSuccess) {
      error.value = true;
      message.value = t('lcp.failed_to_update_user');
      isLoading.value = false;
      return { success: false, updated: false };
    }
    updated = true;
  }

  if (isUserAssociationChanged()) {
    const updateAssociationSuccess = await updateAssociation(
      user.value.uid!,
      organizationUid.value,
      formUser.value.role,
    );

    if (!updateAssociationSuccess) {
      error.value = true;
      message.value = t('lcp.failed_to_update_user_association');
      isLoading.value = false;
      return { success: false, updated: updated };
    }
    updated = true;
    emitAppEvent({ type: 'association:updated', userUids: [user.value.uid!] });
  }

  if (updated) {
    toast(t('lcp.user_updated_successfully'), 'success');
  }
  isLoading.value = false;
  return { success: true, updated };
};

export type NewUser = {
  uid: string;
  name: string;
  password: string;
  qrToken?: string;
  sharedAccount?: boolean;
};

const isAssociationEditableCheck = ref<ReturnType<typeof reporterWithReason>>({ allowed: true, reason: undefined });
watch(
  [user, organization],
  () => {
    const currentAssociation = props.user.associations.find((assoc) => assoc.schoolUnit.uid === organizationUid.value);
    if (currentAssociation) {
      checkAssociationUpdatePermission(
        permissionContext.value,
        createTargetAssociation(props.user, currentAssociation),
        {
          role: currentAssociation.isAdministrator ? 'admin' : currentAssociation.role,
        },
        getCallerAssociation,
        reporterWithReason,
      ).then((result) => {
        isAssociationEditableCheck.value = result;
      });
    } else {
      isAssociationEditableCheck.value = { allowed: false, reason: 'not_permitted' };
    }
  },
  { immediate: true },
);

const isUserEditableCheck = ref<ReturnType<typeof reporterWithReason>>({ allowed: true, reason: undefined });
watch(
  [user],
  () => {
    checkUserUpdatePermission(
      permissionContext.value,
      {
        userId: props.user.uid,
        firstName: 'new first name',
        lastName: 'new last name',
        email: 'newEmail@example.com',
      },
      getCallerAssociations,
      createGetTargetAssociations(props.user),
      getGroupMemberships,
      reporterWithReason,
    ).then((result) => {
      isUserEditableCheck.value = result;
    });
  },
  { immediate: true },
);

const isGuestPropertyEditableCheck = ref<ReturnType<typeof reporterWithReason>>({ allowed: true, reason: undefined });
watch(
  [user],
  () => {
    checkUserUpdatePermission(
      permissionContext.value,
      {
        userId: props.user.uid,
        // TODO: Fix permissions to not allow to edit this
        guest: true,
      },
      getCallerAssociations,
      createGetTargetAssociations(props.user),
      getGroupMemberships,
      reporterWithReason,
    ).then((result) => {
      isGuestPropertyEditableCheck.value = result;
    });
  },
  { immediate: true },
);

const isSuperUser = ref<boolean>(false);
watch(
  [user],
  () => {
    checkSuperUserPermission(permissionContext.value, reporterBoolean).then((result) => {
      isSuperUser.value = result;
    });
  },
  { immediate: true },
);

defineExpose({
  resetForm,
  updateUser: updateUserAndAssociation,
  resetValidation,
});
</script>

<template>
  <v-form ref="formRef">
    <v-row>
      <v-col>
        <v-text-field
          variant="outlined"
          :singleLine="false"
          color="primary"
          density="default"
          clearable
          :disabled="!isUserEditableCheck.allowed"
          clear-icon="mdi-close"
          :label="t('lcp.first_name') + ' *'"
          required
          block
          hide-details="auto"
          v-model="formUser.firstName"
          :rules="[rules.firstName, rules.required, rules.htmlSafe]"
        ></v-text-field>
      </v-col>
      <v-col v-if="!isUserEditableCheck.allowed" cols="auto" class="d-flex align-center pl-0">
        <PermissionLock :reason="isUserEditableCheck.reason" />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          variant="outlined"
          :singleLine="false"
          color="primary"
          density="default"
          clearable
          :disabled="!isUserEditableCheck.allowed"
          clear-icon="mdi-close"
          :label="t('lcp.last_name')"
          v-model="formUser.lastName"
          hide-details="auto"
          :rules="[rules.firstName, rules.htmlSafe]"
        ></v-text-field>
      </v-col>
      <v-col v-if="!isUserEditableCheck.allowed" cols="auto" class="d-flex align-center pl-0">
        <PermissionLock :reason="isUserEditableCheck.reason" />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <RoleSelect
          v-model="formUser.role"
          :options="roleOptions"
          :rules="[rules.role]"
          variant="outlined"
          density="default"
          color="primary"
          hide-details="auto"
          :disabled="!isAssociationEditableCheck.allowed"
          @update:modelValue="() => formRef.validate()"
          :label="t('lcp.role_in', { school: organization.name })"
          required
        ></RoleSelect>
      </v-col>
      <v-col v-if="!isAssociationEditableCheck.allowed" cols="auto" class="d-flex align-center pl-0">
        <PermissionLock :reason="isAssociationEditableCheck.reason" />
      </v-col>

      <v-col cols="auto" class="d-flex">
        <v-tooltip :text="t('lcp.shared_account')" location="top">
          <template #activator="{ props: tooltipProps }">
            <v-field
              v-bind="tooltipProps"
              :label="t('lcp.shared_account')"
              variant="outlined"
              color="primary"
              active
              :disabled="!isGuestPropertyEditableCheck.allowed"
              class="d-flex align-center px-8"
            >
              <v-switch
                v-model="formUser.sharedAccount"
                color="primary"
                hide-details
                inset
                density="compact"
                :disabled="!isGuestPropertyEditableCheck.allowed"
                @click.stop
              ></v-switch>
              <div v-if="!isGuestPropertyEditableCheck.allowed" class="pl-2">
                <PermissionLock :reason="isGuestPropertyEditableCheck.reason" />
              </div>
            </v-field>
          </template>
        </v-tooltip>
      </v-col>
    </v-row>
    <v-row v-if="isAllowedToManageUserOrganizations" dense>
      <v-col class="d-flex justify-end">
        <v-btn class="pa-0" variant="text" size="xs" @click="openManageOrganizationsModal">
          {{ t('lcp.manage_user_organizations') }} <v-icon class="mx-1">mdi-chevron-right</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-text-field
          variant="outlined"
          :singleLine="false"
          clearable
          clear-icon="mdi-close"
          color="primary"
          hide-details="auto"
          :disabled="!isUserEditableCheck.allowed"
          density="default"
          :label="`${t('lcp.email')}  ${formUser.role === 'employee' ? ' *' : ''}`"
          v-model="formUser.email"
          :rules="[rules.email, ...(formUser.role !== 'student' ? [rules.required] : [])]"
          required="true"
          type="email"
        ></v-text-field>
      </v-col>
      <v-col v-if="!isUserEditableCheck.allowed" cols="auto" class="d-flex align-center pl-0">
        <PermissionLock :reason="isUserEditableCheck.reason" />
      </v-col>
    </v-row>
    <v-alert v-if="message" :type="error ? 'error' : 'success'" variant="outlined" border="start" class="mb-6">
      {{ message }}
    </v-alert>
  </v-form>
</template>
